import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Tag,
  Progress,
  Input as AntdInput,
  List,
  Typography,
  Row,
  Col,
  Divider,
  Tooltip,
  Alert,
  Avatar,
} from "antd";
import {
  Button,
  Flex,
  ArrowLeftIcon,
  ArrowRightIcon,
  EditIcon,
  CloseIcon,
  EyeIcon,
  AddIcon,
  CheckmarkCircleIcon,
  InfoIcon,
  RedbangIcon,
  Label,
  Input,
  RetryIcon,
  Skeleton,
  Loader,
  Text as FluentText,
  Dropdown,
  UserFriendsIcon,
  Dialog,
  GridIcon,
  BulletsIcon,
} from "@fluentui/react-northstar";
import {
  LightbulbCircleOutlined,
  WarningOutlined,
  CommentOutlined,
} from "@mui/icons-material";
import "./SingleAssignment.scss";
import { useIntelligentGrading } from "../../../../context/useIntelligentGrading";
import { useAssignment } from "../../../../../../context/AssignmentContext";
import moment from "moment";
import { TextAnnotator } from "react-text-annotate";
import { Editor } from "react-draft-wysiwyg";
import {
  EditorState,
  ContentState,
  convertFromRaw,
  convertToRaw,
} from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import StudentViewDialog from "./StudentViewDialog";
import { toast } from "react-toastify";
import CommentDialog from "../CommentInput/CommentDialog";
import CommentBankDialog from "../CommentBankDialog/CommentBankDialog";
import userSvg from "../../../../../../Assets/images/svg/user.svg";
import { BrainIcon } from "../../../../../../Assets/icons/icons";
import { AttachmentButton } from "./components/common/AttachmentButton";
import { RenderAttachmentContent } from "./components/common/RenderAttachmentContent";
import { CommentsList } from "./components/common/CommentsList";
import FinalEvaluation from "./components/FinalEvaluation";
import EvaluationReportDialog from "./components/EvaluationReportDialog";
import { useTranslation } from "react-i18next";
import { generatePdfBlobFromRef } from "../../../../../../utils/pdf";
import EvaluationReportDialogContent from "./components/EvaluationReportDialogContent";
import {
  ClockCircleTwoTone,
  SyncOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = AntdInput;

const brandColor = "#6264a7";

const getIconForHighlight = (type) => {
  switch (type) {
    case "strength":
      return <CheckmarkCircleIcon style={{ color: "#52c41a" }} />;
    case "insight":
      return <LightbulbCircleOutlined style={{ color: "#1890ff" }} />;
    case "suggestion":
      return <InfoIcon style={{ color: "#faad14" }} />;
    default:
      return <InfoIcon style={{ color: "#faad14" }} />;
  }
};

const ResultBox = ({ children }) => (
  <div className="evaluation-box">{children}</div>
);

const StudentSubmissionCard = ({
  attachments = [],
  isStudentSubmissionLoading,
  isEditingFeedback,
  grade,
  submission,
  modifiedRubrics,
  modifiedConfidence,
  modifiedComments,
  onEditComment,
  onDeleteComment,
}) => {
  const { getOverallScore, handleRegenerate, isLoading } =
    useIntelligentGrading();

  const [isAnnotateMode, setIsAnnotateMode] = useState(false);
  const [selectedResponseType, setSelectedResponseType] = useState("text");

  const [studentViewDialog, setStudentViewDialog] = useState({
    open: false,
    submission: null,
  });

  const handleStudentView = (submission) => {
    setStudentViewDialog({
      open: true,
      submission: {
        name: submission.StudentDatum.StudentName,
        grade,
        confidence: modifiedConfidence,
        timeTaken:
          submission.submissionStatus === "Completed"
            ? submission.teacherGradingResult.timeTaken
            : submission.aiGradingResult.timeTaken,
        rubrics: modifiedRubrics,
        gradingResult:
          submission.submissionStatus === "Completed"
            ? submission.teacherGradingResult
            : submission.aiGradingResult,
        overallScore: getOverallScore(modifiedRubrics),
      },
    });
  };

  const handleCloseStudentView = () => {
    setStudentViewDialog({
      open: false,
      submission: null,
    });
  };

  return (
    <>
      <Card
        className="student-submission-card"
        title="Student Submission"
        extra={
          <Flex gap="gap.small" vAlign="center">
            {isEditingFeedback && (
              <Button
                size="small"
                text
                data-cy="annotate-btn"
                icon={<EditIcon size="small" />}
                content={isAnnotateMode ? "Exit Annotate" : "Annotate"}
                primary
                style={{ marginRight: 8 }}
                onClick={() => setIsAnnotateMode((prev) => !prev)}
              />
            )}
            <Button
              size="small"
              icon={<EyeIcon />}
              style={{ color: "#52c41a" }}
              text
              content="Student View"
              onClick={() => handleStudentView(submission)}
            />
          </Flex>
        }
      >
        <Flex column gap="gap.medium">
          <Flex gap="gap.smaller" space="between">
            <Flex gap="gap.smaller" vAlign="center">
              <Flex
                hAlign="center"
                vAlign="center"
                style={{ width: "16px", height: "16px", color: "#6264a7" }}
              >
                {BrainIcon()}
              </Flex>
              <FluentText weight="semibold" content="AI-Powered Evaluation" />
            </Flex>
            <Flex gap="gap.smaller">
              <Flex gap="gap.smaller" vAlign="center">
                <CheckmarkCircleIcon outline style={{ color: "#52c41a" }} />
                <Text type="success" style={{ fontSize: 14 }}>
                  Analysis Completed
                </Text>
              </Flex>
              <Button
                style={{ padding: "0px 10px" }}
                icon={
                  <Flex
                    hAlign="center"
                    vAlign="center"
                    style={{ width: "16px", height: "16px", color: "#fff" }}
                  >
                    {BrainIcon()}
                  </Flex>
                }
                content="Rerun AI"
                primary
                loading={isLoading}
                disabled={isLoading || !isEditingFeedback}
                onClick={() =>
                  handleRegenerate(submission.id, submission.studentId)
                }
              />
            </Flex>
          </Flex>
          <Row gutter={[5, 5]}>
            {attachments.map((attachment, index) => (
              <Col key={`attachment-${index + 1}`}>
                <AttachmentButton
                  icon={attachment.icon}
                  content={attachment.content}
                  isActive={attachment.type === selectedResponseType}
                  onClick={() => setSelectedResponseType(attachment.type)}
                />
              </Col>
            ))}
          </Row>
        </Flex>
        <Divider />
        {attachments.length > 0 ? (
          <>
            <Flex gap="gap.small" column>
              <RenderAttachmentContent
                attachment={attachments.find(
                  (item) => item.type == selectedResponseType,
                )}
                gradingResult={
                  submission.submissionStatus === "Completed"
                    ? submission.teacherGradingResult
                    : submission.aiGradingResult
                }
                isAnnotationMode={isAnnotateMode}
              />
            </Flex>
          </>
        ) : null}

        <Divider />
        <Text style={{ fontSize: 16 }}>Comments</Text>
        <Divider />
        <CommentsList
          comments={modifiedComments}
          onEditComment={(index, comment) => onEditComment(index, comment)}
          onDeleteComment={(index) => onDeleteComment(index)}
        />
      </Card>
      <StudentViewDialog
        open={studentViewDialog.open}
        onClose={handleCloseStudentView}
        submission={studentViewDialog.submission}
        attachments={attachments}
      />
    </>
  );
};

const AiEvaluation = ({ gradingResult }) => {
  const { getColorBasedOnPoints } = useIntelligentGrading();

  return (
    <Card
      title="AI Evaluation Evidence"
      style={{ marginTop: 15 }}
      extra={
        <Flex gap="gap.smaller" vAlign="center">
          <InfoIcon size="small" outline color="grey" />
          <FluentText size="small" color="grey" content="Explainable AI" />
        </Flex>
      }
    >
      <Flex style={{ marginBottom: 10 }}>
        <FluentText
          size="small"
          style={{ textTransform: "uppercase" }}
          content={`Key Evidence Used:`}
          fontWeight="semibold"
        />
      </Flex>
      <List
        dataSource={gradingResult.rubricEvidence}
        renderItem={(item) => (
          <List.Item>
            <Flex column gap="gap.small" style={{ width: "100%" }}>
              {item.evidence.map((evidence, index) => (
                <Flex key={index} column>
                  <FluentText
                    size="small"
                    content={`"${evidence.text}"`}
                    style={{ color: "grey" }}
                  />
                  <Flex vAlign="center" gap="gap.smaller">
                    <Progress
                      percent={evidence.confidence}
                      showInfo={false}
                      size="small"
                      strokeColor={getColorBasedOnPoints(evidence.confidence)}
                    />
                    <FluentText
                      size="small"
                      content={`${evidence.confidence}%`}
                    />
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </List.Item>
        )}
      />
    </Card>
  );
};

const SingleAssignment = () => {
  const { t } = useTranslation();

  const translation = t("assignment").summary.single;

  const { currentAssignmentData } = useAssignment();

  const evaluationReportRef = useRef(null);
  const evaluationReportRef_ = useRef(null);

  const {
    selectedSubmission,
    selectedSubmissionIndex,
    handleSubmissionChange,
    allAssignmentSubmissions,
    overallScore,
    rubrics,
    submitGrading,
    getGrade,
    isStudentSubmissionLoading,
    isLoading,
    getColorBasedOnPoints,
    annotations,
    handleSendGradingReport,
    setIsSendingReport,
  } = useIntelligentGrading();

  useEffect(() => {
    handleSubmissionChange(selectedSubmissionIndex);
  }, []);

  const isSubmissionDoneByStudent =
    (selectedSubmission?.submissionStatus === "Grades Pending" ||
      selectedSubmission?.submissionStatus === "Completed") &&
    selectedSubmission.aiGradingStatus === "Completed";

  // State to track teacher modifications
  const [modifiedFeedback, setModifiedFeedback] = useState(null);
  const [hasModifications, setHasModifications] = useState(false);
  const [modifiedRubrics, setModifiedRubrics] = useState(rubrics);
  const [modifiedScore, setModifiedScore] = useState(0);
  const [modifiedGrade, setModifiedGrade] = useState("-");
  const [modifiedConfidence, setModifiedConfidence] = useState(0);
  const [modifiedComments, setModifiedComments] = useState([]);
  // Feedback editor state
  const [feedbackEditorState, setFeedbackEditorState] = useState();

  // Overwrite the whole feedback
  const [overwriteFeedback, setOverwriteFeedback] = useState(false);

  // State for edit mode
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);

  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [editingCommentIndex, setEditingCommentIndex] = useState(null);
  const [editingComment, setEditingComment] = useState(null);

  const [isCommentBankOpen, setIsCommentBankOpen] = useState(false);

  const [isStudentDialogOpen, setIsStudentDialogOpen] = useState(false);
  const [studentViewMode, setStudentViewMode] = useState("list"); // 'list' or 'grid'

  const [evaluationReportDialog, setEvaluationReportDialog] = useState({
    type: "", // generate-report-pdf | display-report
    open: false,
  });

  // Resizable panel state
  const [leftWidth, setLeftWidth] = useState(60); // percent
  const containerRef = useRef();

  const selectedStudent = allAssignmentSubmissions[selectedSubmissionIndex];
  const formattedSelectedStudent = {
    header: selectedStudent?.StudentDatum?.StudentName,
    content: <div>{selectedStudent?.StudentDatum?.StudentName}</div>,
    image: selectedStudent?.StudentDatum?.studPhoto?.Foto
      ? selectedStudent?.StudentDatum?.studPhoto?.Foto +
        process.env.REACT_APP_AZURE_SAS_TOKEN.replace(/"/g, "")
      : userSvg,
  };

  const handleInitialFeedback = (submission) => {
    try {
      if (
        submission?.submissionStatus === "Completed" &&
        submission.aiGradingStatus === "Completed"
      ) {
        const initialFeedback =
          submission?.teacherGradingResult?.overallFeedback || "";

        if (initialFeedback && typeof initialFeedback === "string") {
          const blocksFromHtml = htmlToDraft(initialFeedback);
          const { contentBlocks, entityMap } = blocksFromHtml;
          const contentState = ContentState.createFromBlockArray(
            contentBlocks,
            entityMap,
          );
          setFeedbackEditorState(EditorState.createWithContent(contentState));
        } else {
          setFeedbackEditorState(EditorState.createEmpty());
        }

        setModifiedFeedback(submission.teacherGradingResult.overallFeedback);
        setModifiedRubrics(rubrics);
        setModifiedScore(overallScore);
        setModifiedGrade(
          getGrade((overallScore.score / overallScore.max) * 100),
        );
        setModifiedConfidence(
          submission.teacherGradingResult.overallConfidence,
        );
        setModifiedComments(submission?.teacherGradingResult?.comments || []);

        setHasModifications(false);
      } else if (
        submission?.submissionStatus === "Grades Pending" &&
        submission.aiGradingStatus === "Completed"
      ) {
        const initialFeedback =
          submission?.aiGradingResult?.overallFeedback || "";

        if (initialFeedback && typeof initialFeedback === "string") {
          const blocksFromHtml = htmlToDraft(initialFeedback);
          const { contentBlocks, entityMap } = blocksFromHtml;
          const contentState = ContentState.createFromBlockArray(
            contentBlocks,
            entityMap,
          );
          setFeedbackEditorState(EditorState.createWithContent(contentState));
        } else {
          setFeedbackEditorState(EditorState.createEmpty());
        }

        setModifiedFeedback(submission?.aiGradingResult?.overallFeedback || "");
        setModifiedRubrics(rubrics);
        setModifiedScore(overallScore);
        setModifiedGrade(
          getGrade((overallScore.score / overallScore.max) * 100),
        );
        setModifiedConfidence(submission?.aiGradingResult?.overallConfidence);
        setModifiedComments(submission?.aiGradingResult?.comments || []);
        setHasModifications(false);
      }
    } catch (error) {
      console.error("Error handling initial feedback:", error);
      setFeedbackEditorState(EditorState.createEmpty());
    }
  };

  // Initialize states when selectedSubmission changes
  useEffect(() => {
    if (
      selectedSubmission &&
      selectedSubmission.aiGradingStatus === "Completed"
    ) {
      handleInitialFeedback(selectedSubmission);
    }
  }, [selectedSubmission, rubrics, overallScore]);

  // Handle feedback changes
  const onFeedbackChange = (editorState) => {
    setFeedbackEditorState(editorState);
    const contentState = editorState.getCurrentContent();
    const rawContent = convertToRaw(contentState);

    // Convert the raw content to HTML
    const htmlContent = draftToHtml(rawContent);
    setModifiedFeedback(htmlContent);
    setHasModifications(true);
  };

  // Handle rubric score changes
  const handleRubricCriteriaChange = (rubricId, criteriaId, newScore) => {
    try {
      const updatedRubrics = modifiedRubrics.map((item) =>
        item.id === rubricId
          ? {
              ...item,
              criteriaId: criteriaId,
              score: Math.min(Math.max(0, newScore), item.max),
              // confidence: Math.floor((newScore / item.max) * 100),
              color: getColorBasedOnPoints((newScore / item.max) * 100),
            }
          : item,
      );

      // Update overall confidence
      const newTotalConfidence = updatedRubrics.reduce(
        (sum, item) => sum + item.confidence,
        0,
      );
      // setModifiedConfidence(
      //   Math.floor(newTotalConfidence / updatedRubrics.length)
      // );

      setModifiedRubrics(updatedRubrics);

      // Update overall score
      const newTotalScore = updatedRubrics.reduce(
        (sum, item) => sum + item.score,
        0,
      );
      setModifiedScore({
        ...modifiedScore,
        score: newTotalScore,
      });

      setModifiedGrade(getGrade((newTotalScore / modifiedScore.max) * 100));

      setHasModifications(true);
    } catch (error) {
      console.log("error", error);
    }
  };

  // Handle edit button click
  const handleEditFeedback = () => {
    setIsEditingFeedback(true);
  };

  // Handle saving changes
  const handleSaveChanges = () => {
    setIsEditingFeedback(false);
    setHasModifications(true);
  };

  const validateFeedback = () => {
    const isRubricScoreZero = modifiedRubrics.some((item) => item.score === 0);
    if (
      !modifiedFeedback ||
      isRubricScoreZero ||
      modifiedGrade === "-" ||
      modifiedScore.score === 0
    ) {
      return false;
    }
    return true;
  };

  // Update submitGrading call to include modified data
  const handleSubmitGrading = (status) => {
    if (!validateFeedback()) {
      toast.error(translation.validation.pleaseFillDetails);
      return;
    }
    const teacherGradingResult = {
      ...selectedSubmission.aiGradingResult,
      results: modifiedRubrics.map((item) => ({
        ...item,
        confidence: item.confidence,
        criteriaId: item.criteriaId,
        rubricItemId: item.id,
        justification: item.justification,
      })),
      overallFeedback: modifiedFeedback,
      approvedAiGrading: !hasModifications,
      comments: modifiedComments,
      annotations,
    };

    submitGrading(
      status,
      {
        ...selectedSubmission,
        teacherGradingResult,
        overallScore: modifiedScore,
      },
      "", // reason is filled only when status is Resubmit
    );
  };

  const handleSubmitOverridedFeedback = () => {
    if (!validateFeedback()) {
      toast.error(translation.validation.pleaseFillDetails);
      return;
    }

    const teacherGradingResult = {
      results: modifiedRubrics.map((item) => ({
        ...item,
        confidence: item.confidence,
        criteriaId: item.criteriaId,
        rubricItemId: item.id,
        justification: item.justification,
      })),
      highlights: selectedSubmission.aiGradingResult.highlights,
      overallFeedback: modifiedFeedback,
      approvedAiGrading: !hasModifications,
      overallConfidence: modifiedConfidence,
      timeTaken: selectedSubmission.aiGradingResult.timeTaken,
      overWrite: true,
      approvedAiGrading: false,
    };

    submitGrading(
      "Completed",
      {
        ...selectedSubmission,
        teacherGradingResult,
        overallScore: modifiedScore,
      },
      "",
    );
  };

  const handleOverrideFeedback = () => {
    if (overwriteFeedback) {
      setOverwriteFeedback(false);
      const initialFeedback =
        selectedSubmission?.aiGradingResult?.overallFeedback || "";

      if (initialFeedback && typeof initialFeedback === "string") {
        const blocksFromHtml = htmlToDraft(initialFeedback);
        const { contentBlocks, entityMap } = blocksFromHtml;
        const contentState = ContentState.createFromBlockArray(
          contentBlocks,
          entityMap,
        );
        setFeedbackEditorState(EditorState.createWithContent(contentState));
      } else {
        setFeedbackEditorState(EditorState.createEmpty());
      }
      setModifiedRubrics(rubrics);
      setModifiedScore(overallScore);
      setModifiedGrade(getGrade((overallScore.score / overallScore.max) * 100));
      setHasModifications(false);
      setIsEditingFeedback(false);
    } else {
      setOverwriteFeedback(true);
      setFeedbackEditorState(EditorState.createEmpty());
      setModifiedRubrics(
        modifiedRubrics.map((item) => ({ ...item, score: 0 })),
      );
      setModifiedScore({ ...overallScore, score: 0 });
      setModifiedGrade("-");
      setHasModifications(true);
      setIsEditingFeedback(true);
    }
  };

  const handleEditComment = (index, comment) => {
    setEditingCommentIndex(index);
    setEditingComment(comment);
    setIsEditingComment(true);
    setIsCommentDialogOpen(true);
  };

  const handleDeleteComment = (index) => {
    const newComments = [...modifiedComments];
    newComments.splice(index, 1);
    setModifiedComments(newComments);
  };

  const handleCommentSubmit = (comment) => {
    if (isEditingComment && editingCommentIndex !== null) {
      // Handle edit
      const newComments = [...modifiedComments];
      newComments[editingCommentIndex] = comment;
      setModifiedComments(newComments);
      setIsEditingComment(false);
      setEditingCommentIndex(null);
      setEditingComment(null);
    } else {
      // Handle new comment
      setModifiedComments([...modifiedComments, comment]);
    }
  };

  const handleCommentDialogClose = () => {
    setIsCommentDialogOpen(false);
    setIsEditingComment(false);
    setEditingCommentIndex(null);
    setEditingComment(null);
  };

  const handleCommentSelect = (comment) => {
    // Add the selected comment to the feedback

    setModifiedComments([
      ...modifiedComments,
      { text: comment.text, tag: comment.tag },
    ]);
    setIsCommentBankOpen(false);
  };

  const sendGradingReport = async (customRef = null) => {
    try {
      setIsSendingReport(true);
      const targetRef = customRef || evaluationReportRef;
      const blob = await generatePdfBlobFromRef(targetRef);
      handleSendGradingReport(
        currentAssignmentData.assignmentTitle,
        selectedSubmission?.StudentDatum?.email,
        selectedSubmission?.StudentDatum?.StudentName ||
          selectedSubmission?.StudentDatum?.email.split("@")[0],
        blob,
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsSendingReport(false);
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftWidth;

    const onMouseMove = (moveEvent) => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const dx = moveEvent.clientX - startX;
      let newLeftWidth =
        (((startWidth / 100) * containerWidth + dx) / containerWidth) * 100;
      newLeftWidth = Math.max(20, Math.min(80, newLeftWidth)); // clamp between 20% and 80%
      setLeftWidth(newLeftWidth);
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // useEffect(() => {
  //   if (!selectedSubmission) return;

  //   // 🔥 HARD RESET teacher override state

  // }, [selectedSubmission?.id]);

  return (
    <div className="single-assignment-container">
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {translation.header.grading}:{" "}
            {currentAssignmentData.assignmentTitle}
          </Title>

          <Flex gap="gap.small" vAlign="center">
            <Avatar
              name={selectedSubmission?.StudentDatum?.StudentName}
              src={
                selectedSubmission?.StudentDatum?.studPhoto?.Foto
                  ? selectedSubmission?.StudentDatum?.studPhoto?.Foto +
                    process.env.REACT_APP_AZURE_SAS_TOKEN.replace(/"/g, "")
                  : ""
              }
            />
            <div>
              <Text type="secondary" style={{ display: "block" }}>
                {translation.header.submittedBy}{" "}
                <b>{selectedSubmission?.StudentDatum?.StudentName}</b>
              </Text>
              <Text type="secondary" style={{ display: "block" }}>
                {translation.header.due}:{" "}
                {moment(currentAssignmentData.dueDate).format("MMM D, YYYY")}
              </Text>
            </div>
          </Flex>
        </Col>
        <Col>
          <Flex gap="gap.small">
            <Dropdown
              className="student-submissions-dropdown"
              items={allAssignmentSubmissions.map((submission, index) => ({
                key: index,
                header: submission?.StudentDatum?.StudentName,
                content: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {translation.status.submitted}:{" "}
                        {moment(submission?.submittedDate).format(
                          "MMM D, YYYY",
                        )}
                      </div>
                    </div>
                    {submission.submissionStatus === "Completed" && (
                      <div
                        style={{
                          background: `${getColorBasedOnPoints(
                            submission.givenGrade,
                          )}25`,
                          color: getColorBasedOnPoints(submission.givenGrade),
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontSize: "14px",
                        }}
                      >
                        {submission.givenGrade}
                      </div>
                    )}
                    {submission.submissionStatus === "Pending" && (
                      <div
                        style={{
                          background: "#faad1425",
                          color: "#faad14",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {translation.status.pending}
                      </div>
                    )}
                    {submission.submissionStatus === "Grades Pending" && (
                      <div
                        style={{
                          background: "#5d78c925",
                          color: "#5d78c9",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {translation.status.inProgress}
                      </div>
                    )}
                  </div>
                ),
                image: submission?.StudentDatum?.studPhoto?.Foto
                  ? submission?.StudentDatum?.studPhoto?.Foto +
                    process.env.REACT_APP_AZURE_SAS_TOKEN.replace(/"/g, "")
                  : userSvg,
              }))}
              value={formattedSelectedStudent}
              onChange={(e, data) => {
                handleSubmissionChange(data.value.key);
              }}
              style={{ minWidth: "300px", marginRight: "16px" }}
            />
            <Button
              icon={<UserFriendsIcon />}
              iconOnly
              secondary
              onClick={() => setIsStudentDialogOpen(true)}
              text
            />
            <Flex gap="gap.small">
              <Button
                data-cy="prev-submission-btn"
                icon={<ArrowLeftIcon />}
                content={translation.navigation.previous}
                disabled={selectedSubmissionIndex === 0}
                onClick={() =>
                  handleSubmissionChange(selectedSubmissionIndex - 1)
                }
              />
              <Button
                data-cy="next-submission-btn"
                icon={<ArrowRightIcon />}
                iconPosition="after"
                content={translation.navigation.next}
                disabled={
                  selectedSubmissionIndex ===
                  allAssignmentSubmissions.length - 1
                }
                onClick={() => {
                  handleSubmissionChange(selectedSubmissionIndex + 1);
                  setOverwriteFeedback(false);
                  setIsEditingFeedback(false);
                  setHasModifications(false);
                }}
              />
            </Flex>
          </Flex>
        </Col>
      </Row>
      {/* Student Selector Dialog */}
      <Dialog
        open={isStudentDialogOpen}
        onCancel={() => setIsStudentDialogOpen(false)}
        header={translation.header.selectStudent}
        styles={{
          maxWidth: "550px",
        }}
        content={
          <div style={{ minWidth: 400, maxWidth: 550 }}>
            <Flex gap="gap.small" style={{ marginBottom: 20 }}>
              <Button
                content={translation.navigation.list}
                primary={studentViewMode === "list"}
                onClick={() => setStudentViewMode("list")}
                iconOnly={false}
                icon={<BulletsIcon />}
                style={{
                  borderRadius: 5,
                }}
              />
              <Button
                content={translation.navigation.grid}
                primary={studentViewMode === "grid"}
                onClick={() => setStudentViewMode("grid")}
                iconOnly={false}
                icon={<GridIcon />}
                style={{
                  borderRadius: 5,
                }}
              />
            </Flex>
            {studentViewMode === "list" ? (
              <div>
                {allAssignmentSubmissions.map((submission, idx) => {
                  const status = submission.submissionStatus;
                  let statusNode = null;
                  if (status === "Completed") {
                    statusNode = (
                      <span
                        style={{
                          background: "#d3f9df",
                          color: "#389e5f",
                          borderRadius: 12,
                          padding: "2px 12px",
                          fontWeight: 500,
                          fontSize: 15,
                        }}
                      >
                        {submission.givenGrade}
                      </span>
                    );
                  } else if (status === "Pending") {
                    statusNode = (
                      <span
                        style={{
                          background: "#fff4d6",
                          color: "#b88900",
                          borderRadius: 12,
                          padding: "2px 12px",
                          fontWeight: 500,
                          fontSize: 15,
                        }}
                      >
                        {translation.status.pending}
                      </span>
                    );
                  } else if (status === "Grades Pending") {
                    statusNode = (
                      <span
                        style={{
                          background: "#e6f0ff",
                          color: "#3578e5",
                          borderRadius: 12,
                          padding: "2px 12px",
                          fontWeight: 500,
                          fontSize: 15,
                        }}
                      >
                        {translation.status.inProgress}
                      </span>
                    );
                  }
                  return (
                    <Flex
                      key={idx}
                      vAlign="center"
                      gap="gap.medium"
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        marginBottom: 8,
                        background:
                          idx === selectedSubmissionIndex ? "#f4f8ff" : "#fff",
                        cursor: "pointer",
                        border:
                          idx === selectedSubmissionIndex
                            ? "1.5px solid #d6e4ff"
                            : "1.5px solid transparent",
                      }}
                      onClick={() => {
                        handleSubmissionChange(idx);
                        setIsStudentDialogOpen(false);
                      }}
                    >
                      <Avatar
                        name={submission?.StudentDatum?.StudentName}
                        src={
                          submission?.StudentDatum?.studPhoto?.Foto
                            ? submission?.StudentDatum?.studPhoto?.Foto +
                              process.env.REACT_APP_AZURE_SAS_TOKEN.replace(
                                /"/g,
                                "",
                              )
                            : ""
                        }
                        size={36}
                        style={{ marginRight: 8 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 17 }}>
                          {submission?.StudentDatum?.StudentName}
                        </div>
                        <div style={{ color: "#888", fontSize: 14 }}>
                          {translation.status.submitted}:{" "}
                          {moment(submission?.submittedDate).format(
                            "MMM D, YYYY",
                          )}
                        </div>
                      </div>
                      {statusNode}
                    </Flex>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 18,
                }}
              >
                {allAssignmentSubmissions.map((submission, idx) => {
                  const status = submission.submissionStatus;
                  let statusNode = null;
                  if (status === "Completed") {
                    statusNode = (
                      <span
                        style={{
                          background: "#d3f9df",
                          color: "#389e5f",
                          borderRadius: 12,
                          padding: "2px 12px",
                          fontWeight: 500,
                          fontSize: 15,
                        }}
                      >
                        {submission.givenGrade}
                      </span>
                    );
                  } else if (status === "Pending") {
                    statusNode = (
                      <span
                        style={{
                          background: "#fff4d6",
                          color: "#b88900",
                          borderRadius: 12,
                          padding: "2px 12px",
                          fontWeight: 500,
                          fontSize: 15,
                        }}
                      >
                        {translation.status.pending}
                      </span>
                    );
                  } else if (status === "Grades Pending") {
                    statusNode = (
                      <span
                        style={{
                          background: "#e6f0ff",
                          color: "#3578e5",
                          borderRadius: 12,
                          padding: "2px 12px",
                          fontWeight: 500,
                          fontSize: 15,
                        }}
                      >
                        {translation.status.inProgress}
                      </span>
                    );
                  }
                  return (
                    <div
                      key={idx}
                      style={{
                        background:
                          idx === selectedSubmissionIndex ? "#f4f8ff" : "#fff",
                        border:
                          idx === selectedSubmissionIndex
                            ? "1.5px solid #d6e4ff"
                            : "1.5px solid #eee",
                        borderRadius: 16,
                        padding: 18,
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        transition: "box-shadow 0.2s",
                        boxShadow:
                          idx === selectedSubmissionIndex
                            ? "0 2px 8px #d6e4ff55"
                            : "none",
                      }}
                      onClick={() => {
                        handleSubmissionChange(idx);
                        setIsStudentDialogOpen(false);
                      }}
                    >
                      <Avatar
                        name={submission?.StudentDatum?.StudentName}
                        src={
                          submission?.StudentDatum?.studPhoto?.Foto
                            ? submission?.StudentDatum?.studPhoto?.Foto +
                              process.env.REACT_APP_AZURE_SAS_TOKEN.replace(
                                /"/g,
                                "",
                              )
                            : ""
                        }
                        size={54}
                        style={{ marginBottom: 10 }}
                      />
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 17,
                          marginBottom: 4,
                        }}
                      >
                        {submission?.StudentDatum?.StudentName}
                      </div>
                      <div
                        style={{ color: "#888", fontSize: 14, marginBottom: 8 }}
                      >
                        {translation.status.submitted}:{" "}
                        {moment(submission?.submittedDate).format(
                          "MMM D, YYYY",
                        )}
                      </div>
                      {statusNode}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        }
        headerAction={{
          icon: <CloseIcon />,
          title: translation.header.close,
          onClick: () => setIsStudentDialogOpen(false),
        }}
      />
      {isStudentSubmissionLoading ? (
        <Flex
          data-cy="submission-loading-spinner"
          hAlign="center"
          vAlign="center"
        >
          <Loader />
        </Flex>
      ) : isSubmissionDoneByStudent ? (
        <div
          ref={containerRef}
          style={{
            display: "flex",
            width: "100%",
            minHeight: 500,
            position: "relative",
            flexDirection: "row",
          }}
        >
          {/* Left Section */}
          <div
            style={{
              width: `${leftWidth}%`,
              minWidth: 200,
              maxWidth: "80%",
              transition: "width 0.1s",
              paddingRight: 8,
              boxSizing: "border-box",
            }}
          >
            {/* Left: Student Submission */}
            <StudentSubmissionCard
              attachments={selectedSubmission?.attachments}
              isStudentSubmissionLoading={isStudentSubmissionLoading}
              isEditingFeedback={isEditingFeedback}
              grade={modifiedGrade}
              submission={selectedSubmission}
              modifiedRubrics={modifiedRubrics}
              modifiedConfidence={modifiedConfidence}
              modifiedComments={modifiedComments}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
            />

            {/* AI Analysis Highlights */}
            <Card
              title={translation.analysis.aiAnalysisHighlights}
              style={{ marginTop: 15 }}
            >
              <List
                dataSource={
                  selectedSubmission.submissionStatus === "Completed"
                    ? selectedSubmission.teacherGradingResult.highlights
                    : selectedSubmission.aiGradingResult.highlights
                }
                renderItem={(item) => (
                  <List.Item>
                    <Flex vAlign="center" gap="gap.small">
                      {getIconForHighlight(item.type)}
                      <Text>{item.text}</Text>
                    </Flex>
                  </List.Item>
                )}
              />
            </Card>
            <FinalEvaluation
              gradingResult={
                selectedSubmission.submissionStatus === "Completed"
                  ? selectedSubmission.teacherGradingResult
                  : selectedSubmission.aiGradingResult
              }
              submission={selectedSubmission}
              rubrics={modifiedRubrics}
              overallScore={modifiedScore}
              grade={modifiedGrade}
              onGenerateEvaluationReport={() =>
                setEvaluationReportDialog({
                  type: "display-report",
                  open: true,
                })
              }
              handleSubmitGrading={handleSubmitGrading}
              isLoading={isLoading}
              isEditingFeedback={isEditingFeedback}
              sendGradingReport={sendGradingReport}
              ref={evaluationReportRef_}
            />
          </div>

          {/* Draggable Divider */}
          <div
            style={{
              width: 1,
              cursor: "col-resize",
              zIndex: 2,
              position: "relative",
              userSelect: "none",
            }}
            onMouseDown={handleMouseDown}
          />

          {/* Right Section */}
          <div
            style={{
              flex: 1,
              minWidth: 200,
              maxWidth: "80%",
              paddingLeft: 8,
              boxSizing: "border-box",
            }}
            className="evaluation-container"
          >
            {/* Right: Evaluation */}
            <Card
              title={translation.evaluation.suggestedEvaluation}
              extra={
                <Label
                  circular
                  styles={{ backgroundColor: "#eae6ff", color: "#6264a7" }}
                  content={`${translation.evaluation.aiConfidence}: ${modifiedConfidence}%`}
                />
              }
              style={{ marginBottom: 16 }}
              className="evaluation-card"
            >
              <Row
                gutter={10}
                style={{ marginBottom: 8 }}
                className="evaluation-row"
              >
                <Col span={8} className="evaluation-col">
                  <ResultBox>
                    <Text style={{ fontSize: 14 }} type="secondary">
                      {translation.evaluation.suggestedGrade}
                    </Text>
                    <Title
                      level={4}
                      style={{ margin: 0, borderBottom: "1px solid #e6e6e6" }}
                    >
                      {modifiedGrade}
                    </Title>
                  </ResultBox>
                </Col>
                <Col span={8} className="evaluation-col">
                  <ResultBox>
                    <Text style={{ fontSize: 14 }} type="secondary">
                      {translation.evaluation.points}
                    </Text>
                    <Flex gap="gap.smaller" vAlign="center">
                      <Title
                        level={4}
                        style={{
                          margin: 0,
                          borderBottom: "1px solid #e6e6e6",
                        }}
                      >
                        {modifiedScore.score} / {modifiedScore.max}
                      </Title>
                    </Flex>
                  </ResultBox>
                </Col>
                {!overwriteFeedback && (
                  <Col span={8} className="evaluation-col">
                    <ResultBox>
                      <Text style={{ fontSize: 14 }} type="secondary">
                        {translation.evaluation.timeTaken}
                      </Text>
                      <Title
                        level={4}
                        style={{
                          margin: 0,
                          borderBottom: "1px solid #e6e6e6",
                        }}
                      >
                        {String(
                          Math.floor(
                            selectedSubmission.aiGradingResult.timeTaken / 60,
                          ),
                        ).padStart(2, "0")}
                        :
                        {String(
                          Math.floor(
                            selectedSubmission.aiGradingResult.timeTaken % 60,
                          ),
                        ).padStart(2, "0")}
                      </Title>
                    </ResultBox>
                  </Col>
                )}
              </Row>
              <Divider />

              <Text>
                {translation.evaluation.aiGeneratedFeedback} (
                {currentAssignmentData.settings.feedbackTone})
              </Text>
              <div
                style={{
                  background: "#fafbfc",
                  border: "1.5px solid #e6e6e6",
                  borderRadius: 8,
                  padding: 12,
                  margin: "8px 0",
                }}
              >
                <Editor
                  editorState={feedbackEditorState}
                  onEditorStateChange={onFeedbackChange}
                  toolbarClassName="toolbarClassName"
                  wrapperClassName="wrapperClassName"
                  editorClassName="editorClassName ai-generated-feedback-editor"
                  readOnly={!isEditingFeedback}
                  toolbarHidden={!isEditingFeedback}
                  editorStyle={{
                    minHeight: 100,
                    background: "#fff",
                    borderRadius: 6,
                    padding: 8,
                    cursor: isEditingFeedback ? "text" : "default",
                  }}
                  toolbar={{
                    inline: { options: ["bold", "italic", "underline"] },
                    list: { options: ["unordered", "ordered"] },
                    textAlign: {},
                    link: {},
                    emoji: {},
                    options: ["inline", "list", "textAlign", "link", "emoji"],
                  }}
                />
              </div>
              {selectedSubmission.submissionStatus === "Grades Pending" && (
                <>
                  <Flex column gap="gap.small">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {isEditingFeedback
                        ? translation.evaluation.editingMode
                        : translation.evaluation.viewOnlyMode}
                    </Text>
                    <Flex gap="gap.small">
                      <Button
                        text
                        icon={<CommentOutlined />}
                        content={translation.actions.addComment}
                        data-cy="add-comment-btn"
                        primary
                        disabled={!isEditingFeedback}
                        onClick={() => setIsCommentDialogOpen(true)}
                      />
                      <Button
                        data-cy="add-comment-from-bank-btn"
                        text
                        icon={<AddIcon />}
                        content={translation.actions.addCommentFromBank}
                        primary
                        disabled={!isEditingFeedback}
                        onClick={() => setIsCommentBankOpen(true)}
                      />
                    </Flex>

                    <Row gutter={[10, 10]}>
                      <Col span={24}>
                        <Button
                          fluid
                          primary
                          data-cy="approve-feedback-submission-btn"
                          icon={<CheckmarkCircleIcon />}
                          content={
                            overwriteFeedback
                              ? translation.actions.approveSubmission
                              : translation.actions.approveAiSuggestion
                          }
                          styles={{
                            background: "#52c41a",
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            ":hover": {
                              background: "#43a047",
                              borderColor: "#43a047",
                            },
                          }}
                          title={
                            overwriteFeedback
                              ? translation.actions.approveSubmission
                              : translation.actions.approveAiSuggestion
                          }
                          onClick={() =>
                            overwriteFeedback
                              ? handleSubmitOverridedFeedback()
                              : handleSubmitGrading("Completed")
                          }
                          disabled={isEditingFeedback || isLoading}
                          loading={isLoading}
                        />
                      </Col>
                      <Col span={12}>
                        <Button
                          fluid
                          data-cy="edit-feedback-btn"
                          primary
                          icon={<EditIcon />}
                          content={
                            isEditingFeedback
                              ? translation.actions.saveChanges
                              : translation.actions.editFeedback
                          }
                          title={
                            isEditingFeedback
                              ? translation.actions.saveChanges
                              : translation.actions.editFeedback
                          }
                          id="edit-feedback"
                          onClick={
                            isEditingFeedback
                              ? handleSaveChanges
                              : handleEditFeedback
                          }
                        />
                      </Col>
                      <Col span={12}>
                        <Button 
                          fluid
                          data-cy="overwrite-feedback-btn"
                          icon={
                            overwriteFeedback ? <CloseIcon /> : <RetryIcon />
                          }
                          content={ 
                            overwriteFeedback
                              ? translation.actions.cancelOverride
                              : translation.actions.overrideCompletely
                          }
                          danger
                          title={translation.actions.overrideCompletely}
                          // disabled={isEditingFeedback}
                          onClick={() => handleOverrideFeedback()}
                        />
                      </Col>
                    </Row>
                  </Flex>
                </>
              )}
              {selectedSubmission.submissionStatus === "Completed" && (
                <Flex gap="gap.small" column>
                  <Flex gap="gap.small">
                    <Button
                      text
                      data-cy="add-comment-btn"
                      icon={<CommentOutlined />}
                      content={translation.actions.addComment}
                      primary
                      disabled={!isEditingFeedback}
                      onClick={() => setIsCommentDialogOpen(true)}
                    />
                    <Button
                      text
                      data-cy="add-comments-from-bank-btn"
                      icon={<AddIcon />}
                      content={translation.actions.addCommentFromBank}
                      primary
                      disabled={!isEditingFeedback}
                      onClick={() => setIsCommentBankOpen(true)}
                    />
                  </Flex>
                  <Button
                    fluid
                    primary
                    icon={<EditIcon />}
                    content={
                      isEditingFeedback
                        ? translation.actions.saveChanges
                        : translation.actions.editFeedback
                    }
                    title={
                      isEditingFeedback
                        ? translation.actions.saveChanges
                        : translation.actions.editFeedback
                    }
                    disabled={isLoading}
                    loading={isLoading}
                    id="edit-feedback"
                    onClick={() => {
                      if (isEditingFeedback) {
                        handleSaveChanges();
                        handleSubmitGrading("Completed");
                      } else {
                        handleEditFeedback();
                      }
                    }}
                  />
                </Flex>
              )}
            </Card>

            {/* Rubric Evaluation */}
            <Card title={translation.evaluation.rubricEvaluation}>
              <List
                dataSource={modifiedRubrics}
                renderItem={(item) => (
                  <List.Item>
                    <div style={{ width: "100%" }} data-cy="rubric-score">
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Text strong>
                            {item.label} ({item.max}{" "}
                            {translation.evaluation.points})
                          </Text>
                        </Col>
                        <Col>
                          <Text>
                            {item.score}/{item.max}
                          </Text>
                        </Col>
                      </Row>
                      <Progress
                        percent={Math.round((item.score / item.max) * 100)}
                        showInfo={false}
                        strokeColor={item.color}
                        style={{ margin: "4px 0" }}
                      />
                      {isEditingFeedback && (
                        <Flex gap="gap.small" hAlign="end">
                          {item.options.map((option) => (
                            <Button
                              data-cy="rubric-score-btn"
                              key={option.criteriaId}
                              content={option.points}
                              iconOnly
                              primary={option.points === item.score}
                              onClick={() =>
                                handleRubricCriteriaChange(
                                  item.id,
                                  option.criteriaId,
                                  option.points,
                                )
                              }
                            />
                          ))}
                        </Flex>
                      )}
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {translation.evaluation.confidence}:{" "}
                            {item.confidence}%
                          </Text>
                        </Col>
                        <Col>
                          <Tooltip title={item.comment}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.comment}
                            </Text>
                          </Tooltip>
                        </Col>
                      </Row>
                    </div>
                  </List.Item>
                )}
              />
            </Card>

            <AiEvaluation
              gradingResult={
                selectedSubmission.submissionStatus === "Completed"
                  ? selectedSubmission.teacherGradingResult
                  : selectedSubmission.aiGradingResult
              }
            />
          </div>
        </div>
      ) : (
        <Card
          className="submission-pending-container"
          style={{
            textAlign: "center",
            maxWidth: 600,
            margin: "32px auto",
            borderRadius: 8,
          }}
          hoverable
        >
          <div style={{ padding: "24px" }}>
            {selectedSubmission.submissionStatus === "Pending" ? (
              <Title
                level={4}
                style={{ color: "#faad14", marginBottom: "24px" }}
              >
                <Flex vAlign="center" hAlign="center">
                  <WarningOutlined style={{ marginRight: 8 }} />
                  {translation.status.submissionPending}
                </Flex>
              </Title>
            ) : selectedSubmission.submissionStatus === "Resubmit" ? (
              <Title
                level={4}
                style={{ color: "#faad14", marginBottom: "24px" }}
              >
                <Flex vAlign="center" hAlign="center">
                  <WarningOutlined style={{ marginRight: 8 }} />
                  Resubmission is Pending
                </Flex>
              </Title>
            ) : selectedSubmission.aiGradingStatus === "Pending" ? (
              <Title
                level={4}
                style={{ color: "#faad14", marginBottom: "24px" }}
              >
                <Flex vAlign="center" hAlign="center">
                  <ClockCircleTwoTone
                    twoToneColor="#faad14"
                    style={{ marginRight: 8 }}
                  />
                  AI Grading is currently Pending
                </Flex>
              </Title>
            ) : selectedSubmission.aiGradingStatus === "Processing" ? (
              <Title
                level={4}
                style={{ marginBottom: "24px", color: "#1890ff" }}
              >
                <Flex vAlign="center" hAlign="center">
                  <SyncOutlined
                    spin
                    twoToneColor="blue"
                    style={{ marginRight: 8 }}
                  />
                  AI Grading is currently in Progress
                </Flex>
              </Title>
            ) : (
              selectedSubmission.aiGradingStatus === "Failed" && (
                <Title
                  level={4}
                  style={{ marginBottom: "24px", color: "#ff5456" }}
                >
                  <Flex vAlign="center" hAlign="center" gap="gap.smaller">
                    <CloseCircleOutlined
                      twoToneColor="#ff5456"
                      style={{ marginRight: 8 }}
                    />
                    AI Grading has failed
                  </Flex>
                </Title>
              )
            )}

            <div
              style={{
                background: "#fafafa",
                padding: "16px",
                borderRadius: 6,
                marginBottom: 24,
              }}
            >
              <Text type="secondary" style={{ fontSize: 16 }}>
                {translation.student.student}:{" "}
                <Text strong style={{ color: brandColor }}>
                  {selectedSubmission?.StudentDatum?.StudentName}
                </Text>
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 16 }}>
                {translation.student.dueDate}:{" "}
                <Text strong style={{ color: brandColor }}>
                  {moment(currentAssignmentData.dueDate).format("MMM D, YYYY")}
                </Text>
              </Text>
            </div>

            {(() => {
              if (selectedSubmission.submissionStatus === "Pending") {
                return (
                  <Alert
                    message={translation.student.pendingSubmissionMessage}
                    type="warning"
                    showIcon
                    style={{ textAlign: "left", borderRadius: "8px" }}
                  />
                );
              } else if (selectedSubmission.aiGradingStatus === "Pending") {
                return (
                  <Alert
                    message={
                      translation.student.aiGradingPendingMessage ||
                      "AI grading is pending. Please wait for the evaluation to complete."
                    }
                    type="warning"
                    showIcon
                    style={{ textAlign: "left", borderRadius: "8px" }}
                  />
                );
              } else if (selectedSubmission.aiGradingStatus === "Processing") {
                return (
                  <Alert
                    message={
                      translation.student.aiGradingProcessingMessage ||
                      "AI grading is currently being processed. Please wait for the evaluation to complete."
                    }
                    type="info"
                    showIcon
                    style={{ textAlign: "left", borderRadius: "8px" }}
                  />
                );
              } else if (selectedSubmission.aiGradingStatus === "Failed") {
                return (
                  <Alert
                    message={
                      translation.student.aiGradingFailedMessage ||
                      "AI grading has failed. Please try again or contact support."
                    }
                    type="error"
                    showIcon
                    style={{ textAlign: "left", borderRadius: "8px" }}
                  />
                );
              }
              return null;
            })()}

            {/* <Alert
              message={translation.student.pendingSubmissionMessage}
              type="warning"
              showIcon
              style={{ textAlign: "left" }}
            /> */}
          </div>
        </Card>
      )}
      {/* Dialogs and invisible report wrapper */}
      <CommentDialog
        open={isCommentDialogOpen}
        onClose={handleCommentDialogClose}
        onSubmit={handleCommentSubmit}
        disabled={!isEditingFeedback}
        editingComment={editingComment}
        isEditing={isEditingComment}
      />
      <CommentBankDialog
        isOpen={isCommentBankOpen}
        onDismiss={() => setIsCommentBankOpen(false)}
        onSelectComment={handleCommentSelect}
      />
      {selectedSubmission && isSubmissionDoneByStudent && (
        <div
          className="invisible-report-wrapper"
          style={{
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
            opacity: 0,
            pointerEvents: "none",
          }}
        >
          <EvaluationReportDialogContent
            gradingResult={
              selectedSubmission?.submissionStatus === "Completed"
                ? selectedSubmission?.teacherGradingResult
                : selectedSubmission?.aiGradingResult
            }
            rubrics={modifiedRubrics}
            studentName={selectedSubmission?.StudentDatum?.StudentName}
            ref={evaluationReportRef_}
            translation={translation}
            sendGradingReport={sendGradingReport}
          />
        </div>
      )}
      {selectedSubmission && isSubmissionDoneByStudent && (
        <div
          className="invisible-dialog-wrapper"
          style={{
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
            opacity: 0,
            pointerEvents: "none",
          }}
        >
          <EvaluationReportDialog
            open={evaluationReportDialog.open}
            onClose={() =>
              setEvaluationReportDialog({
                type: "",
                open: false,
              })
            }
            gradingResult={
              selectedSubmission.submissionStatus === "Completed"
                ? selectedSubmission.teacherGradingResult
                : selectedSubmission.aiGradingResult
            }
            rubrics={modifiedRubrics}
            studentName={selectedSubmission?.StudentDatum?.StudentName}
            sendGradingReport={sendGradingReport}
            type={evaluationReportDialog.type}
            ref={evaluationReportRef}
          />
        </div>
      )}
    </div>
  );
};
export default SingleAssignment;
